import { Calendar } from '@berlitz/icons'
import PageTitle from '@components/PageTitle'
import PageWithRightSidebarTpl from '@components/PageWithRIghtSidebarTpl'
import WithLessonUtilsHOC, { WithLessonUtilsProp } from '@hocs/WithLessonUtils'
import WithPortalAuthorization from '@hocs/WithPortalAuthorization'
import WithUserTimeHOC, { WithUserTimeProps } from '@hocs/WithUserTime'
import { UserProfileProps } from '@interfaces/UserProfile'
import { EventAttendeeProvider } from '@layouts/Events/context'
import { utcfyDate } from '@layouts/Events/helpers'
import Layout from '@layouts/MyLessons/Layout'
import QueryPageData, { QueryPageResults } from '@layouts/MyLessons/QueryPageData'
import { LessonDate } from '@layouts/MySchedule/Layout/interface'
import { GET_PAGE_DATA, getDateLessonCount } from '@layouts/MySchedule/QueryPageData'
import Sidebar from '@layouts/MySchedule/Sidebar'
import SchedulingLayout from '@layouts/Scheduling/SchedulingLayout'
import { setDate as setDateAction } from '@redux/actions/mySchedules'
import { removeOrientation } from '@utils/helpers/sessions'
import get from 'lodash/get'
import moment from 'moment'
import * as React from 'react'
import { withApollo, WithApolloClient } from 'react-apollo'
import { connect } from 'react-redux'
import { bindActionCreators, compose } from 'redux'

interface MyLessonsProp {
  profile: UserProfileProps
  date: string
  setDate: (val: string) => void
}

type Props = WithApolloClient<MyLessonsProp & WithUserTimeProps & WithLessonUtilsProp>

const MyLessons: React.FC<Props> = ({
  profile,
  client,
  date,
  setDate,
  getUserDate,
  getDateWithUserTimezone,
  toUserDate,
  ...props
}) => {
  let d = getDateWithUserTimezone(date)
  if (!d.isValid()) {
    d = getUserDate()
  }

  const [currentDate, setCurrentDate] = React.useState(d)
  const [fetchingLessonDates, setFetchingLessonDates] = React.useState(false)
  const [lessonDates, setLessonDates] = React.useState([] as LessonDate[])

  const id = profile.SFId
  const instructorName = [
    get(profile, 'InstructorProfile.instructorInfo.contactInfo.FirstName'),
    get(profile, 'InstructorProfile.instructorInfo.contactInfo.LastName'),
  ].join(' ')
  const startDate = moment.utc(currentDate.startOf('day').add(1, 's')).format()
  const endDate = moment.utc(currentDate.endOf('day')).format()

  const handleDate = (passedDate: Date) => {
    const selectedDate = moment(passedDate)
    const currentUserDate = getUserDate()
      .set('year', parseInt(selectedDate.format('YYYY'), 10))
      .set('month', parseInt(selectedDate.format('M'), 10) - 1)
      .set('date', parseInt(selectedDate.format('D'), 10))

    setCurrentDate(currentUserDate)

    setDate(currentUserDate.format())
  }

  const handleMonthChange = async (monthDate: Date, didMount = false) => {
    if (!currentDate.isSame(moment(monthDate)) || didMount) {
      setFetchingLessonDates(true)

      try {
        const params = {
          InstructorId: profile.SFId,
          StartDateTime: moment.utc(moment(monthDate).startOf('month').startOf('week')).format(),
          EndDateTime: moment.utc(moment(monthDate).endOf('month').endOf('week')).format(),
          FILTER_TYPE: 'Groups',
        }

        const res = await client.query({
          query: GET_PAGE_DATA,
          variables: { params },
          fetchPolicy: 'network-only',
        })

        let lessons = removeOrientation(
          get(res.data, 'getLessonGroups[0].InstructorGroupInfo.GroupClassList', []) || []
        )
        lessons = props.onlyValidLessons(lessons)

        setFetchingLessonDates(false)
        setLessonDates([...lessonDates, ...getDateLessonCount(lessons, toUserDate)])
      } catch (error) {
        setFetchingLessonDates(false)
      }
    }
  }

  React.useEffect(() => {
    handleMonthChange(moment(startDate).toDate(), true)
  }, [])

  return (
    <QueryPageData InstructorId={id} StartDateTime={startDate} EndDateTime={endDate}>
      {({ loading, data }: QueryPageResults) => {
        return (
          <EventAttendeeProvider
            StartDateTime={utcfyDate(
              currentDate.clone().tz(profile?.Timezone).startOf('month').startOf('week').startOf('day')
            )}
            EndDateTime={utcfyDate(currentDate.clone().tz(profile?.Timezone).endOf('month').endOf('week').endOf('day'))}
            RecordId={profile?.SFId}
            Type={profile?.Type}
          >
            <PageTitle title={'Instructor scheduling'} />
            <PageWithRightSidebarTpl
              body={
                <SchedulingLayout role="instructor" activeTab={2} showClock>
                  <Layout
                    instructor={id}
                    instructorName={instructorName}
                    setDate={handleDate}
                    date={currentDate}
                    startDate={startDate}
                    endDate={endDate}
                    data={data}
                    loading={loading}
                    profile={profile}
                    onMonthChange={handleMonthChange}
                    lessonDates={lessonDates}
                    fetchingLessonDates={fetchingLessonDates}
                  />
                </SchedulingLayout>
              }
              sidebar={
                <Sidebar
                  currentDate={currentDate}
                  setCurrentDate={(date: Date) => handleDate(date)}
                  onMonthChange={handleMonthChange}
                  lessonDates={lessonDates}
                />
              }
              toggleIcon={<Calendar size="md" />}
              showSidebar={false}
              showToggleOnMobile
            />
          </EventAttendeeProvider>
        )
      }}
    </QueryPageData>
  )
}

function mapStateToProps(state: { mySchedules: { date: string } }) {
  return {
    date: state.mySchedules.date,
  }
}

function mapDispatchToProps(dispatch: any) {
  return {
    setDate: bindActionCreators(setDateAction, dispatch),
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)

export default WithPortalAuthorization(
  compose(withConnect, WithUserTimeHOC, WithLessonUtilsHOC, withApollo)(MyLessons),
  {
    id: 'page::instructors::scheduling::my-lessons',
  }
)
